import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase, authCallbackParams } from '../../lib/supabase'
import { describeRecoveryFailure } from '../../utils/authCallback'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import logger from '../../utils/logger'

type Status = 'checking' | 'ready' | 'invalid'

export default function UpdatePassword() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [status, setStatus] = useState<Status>('checking')
  const [invalidReason, setInvalidReason] = useState<string>('')
  const navigate = useNavigate()

  // Ustal, czy mamy ważną sesję resetu. getSession() czeka wewnętrznie na
  // initializePromise klienta, więc wraca dopiero po tym, jak supabase-js
  // spróbuje wymienić kod z URL-a na sesję — nie ma tu wyścigu.
  useEffect(() => {
    let cancelled = false

    // Błąd zwrócony wprost przez Supabase w linku (np. otp_expired) jest
    // rozstrzygający — nie ma po co pytać o sesję.
    if (authCallbackParams.error || authCallbackParams.errorCode) {
      logger.debug('🔑 Reset hasła: błąd w URL callbacku', authCallbackParams.errorCode)
      setInvalidReason(describeRecoveryFailure(authCallbackParams))
      setStatus('invalid')
      return
    }

    supabase.auth
      .getSession()
      .then(({ data: { session }, error: sessionError }) => {
        if (cancelled) return
        if (sessionError) {
          logger.error('❌ Reset hasła: błąd odczytu sesji', sessionError)
        }
        if (session) {
          setStatus('ready')
        } else {
          setInvalidReason(describeRecoveryFailure(authCallbackParams))
          setStatus('invalid')
        }
      })
      .catch((err) => {
        if (cancelled) return
        logger.error('❌ Reset hasła: nie udało się sprawdzić sesji', err)
        setInvalidReason(describeRecoveryFailure(authCallbackParams))
        setStatus('invalid')
      })

    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 8) {
      setError('Hasło musi mieć co najmniej 8 znaków')
      return
    }
    if (password !== confirm) {
      setError('Hasła nie są identyczne')
      return
    }
    setLoading(true)
    setError(null)
    setSuccess(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setInvalidReason(describeRecoveryFailure(authCallbackParams))
        setStatus('invalid')
        return
      }
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      setSuccess('Hasło zostało zmienione. Zaloguj się nowym hasłem.')
      // Wyloguj po zmianie hasła i przejdź do logowania
      try { await supabase.auth.signOut({ scope: 'global' }) } catch {}
      setTimeout(() => navigate('/auth/login'), 1200)
    } catch (err: any) {
      setError(err.message || 'Nie udało się zmienić hasła')
    } finally {
      setLoading(false)
    }
  }

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md card-glass p-8">{children}</div>
    </div>
  )

  if (status === 'checking') {
    return shell(
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-slate-600 text-sm">Sprawdzamy link do zmiany hasła...</p>
      </div>
    )
  }

  if (status === 'invalid') {
    return shell(
      <>
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-slate-800">Link nie zadziałał</h2>
        </div>
        <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          {invalidReason}
        </div>
        <Button className="w-full" onClick={() => navigate('/auth/forgot-password')}>
          Wyślij nowy link
        </Button>
        <div className="mt-6 text-center text-sm">
          <Link to="/auth/login" className="text-blue-600 hover:text-blue-700">Wróć do logowania</Link>
        </div>
      </>
    )
  }

  return shell(
    <>
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-slate-800">Ustaw nowe hasło</h2>
        <p className="text-sm text-slate-600 mt-1">Wprowadź nowe hasło do swojego konta.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">Nowe hasło</label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min. 8 znaków"
            required
            className="w-full bg-white border-slate-200"
          />
        </div>
        <div>
          <label htmlFor="confirm" className="block text-sm font-medium text-slate-700 mb-1">Powtórz hasło</label>
          <Input
            id="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="w-full bg-white border-slate-200"
          />
        </div>

        <Button type="submit" disabled={loading} className="w-full">
          {loading ? 'Zapisywanie...' : 'Zmień hasło'}
        </Button>
      </form>
    </>
  )
}
